import { isArray, isNumber, isString } from 'lodash';
import { QuestionnaireResultItemsQuery } from '../../graphql/graphql';
import { QuestionnaireItems } from '../HealthData/Enums/QuestionnaireItems.enum';
import { getMomentDate } from '../../Utils/dates.util';
import { ITableColumn } from '../../Models/Table/ITableColumn';

const UNITS = {
	[QuestionnaireItems.ACTUAL_WEIGHT]: 'Kg',
	[QuestionnaireItems.CTG]: '',
	[QuestionnaireItems.DIASTOLIC_BLOOD_PRESSURE]: '',
	[QuestionnaireItems.SYSTOLIC_BLOOD_PRESSURE]: '',
	[QuestionnaireItems.WEIGHT_BEFORE_PREGNANCY]: 'Kg',
	[QuestionnaireItems.TOTAL_NUMBER_OF_PREGNANCIES]: '',
	[QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION]: '',
	[QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT]: 'x',
};

interface IModifyQuestionnaireResultItemsToHealthData {
	items?: QuestionnaireResultItemsQuery;
	t: (key: string | undefined, params?: any) => string | undefined;
	patientId: string;
}

export const modifyQuestionnaireResultItemsToHealthData = ({
	items,
	t,
	patientId,
}: IModifyQuestionnaireResultItemsToHealthData) => {
	if (!items?.questionnaireResultItems?.data) {
		return {};
	}

	const filteredData = items.questionnaireResultItems.data.reduce(
		//@ts-ignore
		(acc, { id, question, valueJson, questionnaireResult }) => {
			const identifier = question.key ?? question.title;
			const value = valueJson.rawValue;

			if (!identifier) {
				return { ...acc };
			}

			return {
				...acc,
				[identifier]: [
					{
						key: identifier,
						description: question.key
							? t(`base.${question.key}`)
							: question.title,
						//@ts-ignore
						unit: question.key ? UNITS[question.key] : '',
						data: [
							{
								measure_name: question.key,
								questionnaire: {
									id,
									code: question.questionnaire?.code,
									createdAt: questionnaireResult?.createdAt,
									parentQuestionnaireId: question.parentQuestionnaireItem?.id,
									partnerId: question.questionnaire?.partnerId,
									title: question.title,
									type: question.type,
								},
								questionnaireItemId: question.questionnaire?.id,
								source: {
									code: question.questionnaire?.code,
									title: question.title,
								},
								userId: patientId,
								time: questionnaireResult?.createdAt,
								value: isNumber(value)
									? value +
									  ' ' +
									  //@ts-ignore
									  (UNITS[question.key] ?? '')
									: isNumber(Number(String(value).replace(/x/g, ''))) &&
									  !isNaN(Number(String(value).replace(/x/g, '')))
									? String(value).replace(/x/g, '')
									: isString(value)
									? value
									: isArray(value)
									? value
									: '-',
							},
						],
					},
				],
			};
		},
		{},
	);

	return filteredData;
};

export const modifyQuestionnaireResultChartToHealthData = (
	data: any,
	questionnaireCode: string,
	questionnaireName: string | null,
	t: (key: string, params?: any) => string | undefined,
	roundValuesTo?: number,
) => {
	const modifiedData: any[] = [];

	const apiData = data?.data;
	Object.keys(apiData)
		.map(h => apiData[h])
		.map(item => {
			const itemData: any[] = [];
			const key =
				item.key ??
				(questionnaireCode + item.title)
					.replace(/\s/g, '')
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '');
			let unit: string | undefined = undefined;
			item.values.map((val: any) => {
				itemData.push({
					id: getMomentDate(val.data).format('x'),
					time: val.data,
					value: val.value,
					minWidth: '200px',
					questionnaire: questionnaireName
						? { title: val.description ?? questionnaireName }
						: null,
					textColor: val?.settings?.colors?.textColor,
					backgroundColor: val?.settings?.colors?.backgroundColor,
				});

				if (val.settings?.unit) {
					unit = val.settings.unit;
				}
			});
			if (key) {
				modifiedData.push({
					key: key,
					description: item.title ?? t('base.' + item.key),
					data: itemData,
					unit,
					roundValuesTo: roundValuesTo,
				});
			}
		});

	return modifiedData;
};

export const groupDataByTitle = (data: any) => {
	const groupedData: any[] = [];
	data.data.forEach((item: any) => {
		const indexed = groupedData.find((i: any) => i.title === item.title);
		if (!indexed) {
			groupedData.push(item);
		} else {
			indexed.values.push(...item.values);
		}
	});
	return { data: groupedData };
};
